// src/services/exportCsv.js
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Fetch docs from a collection with optional filters.
 * - Date range runs on the server (single-field index on createdAt, no composite index needed).
 * - Status + text search run client-side to avoid composite-index errors.
 */
export async function fetchForExport({
  collectionName,
  dateField = "createdAt",
  from,        // "YYYY-MM-DD" or ""
  to,          // "YYYY-MM-DD" or ""
  status,      // "" = all
  statusField = "status",
  search,      // free text, matched against all values
}) {
  const constraints = [];
  if (from) constraints.push(where(dateField, ">=", Timestamp.fromDate(new Date(`${from}T00:00:00`))));
  if (to) constraints.push(where(dateField, "<=", Timestamp.fromDate(new Date(`${to}T23:59:59`))));
  if (from || to) constraints.push(orderBy(dateField, "desc"));

  const snap = await getDocs(query(collection(db, collectionName), ...constraints));
  let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (status) rows = rows.filter((r) => r[statusField] === status);
  if (search) {
    const s = search.toLowerCase();
    rows = rows.filter((r) => JSON.stringify(flatten(r)).toLowerCase().includes(s));
  }
  return rows;
}

// Flatten nested objects: { address: { city } } -> { "address.city" }
function flatten(obj, prefix = "", out = {}) {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v.toDate === "function") out[key] = v.toDate().toISOString(); // Timestamp
    else if (v && typeof v.latitude === "number" && typeof v.longitude === "number")
      out[key] = `${v.latitude},${v.longitude}`; // GeoPoint
    else if (Array.isArray(v)) out[key] = v.map((x) => (typeof x === "object" ? JSON.stringify(x) : x)).join(" | ");
    else if (v && typeof v === "object") flatten(v, key, out);
    else out[key] = v ?? "";
  }
  return out;
}

function escapeCell(v) {
  const s = String(v ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows, columns) {
  const flat = rows.map((r) => flatten(r));
  const cols = columns?.length
    ? columns
    : [...new Set(flat.flatMap((r) => Object.keys(r)))];
  const lines = [cols.join(","), ...flat.map((r) => cols.map((c) => escapeCell(r[c])).join(","))];
  return lines.join("\r\n");
}

export function downloadCsv(csv, filename) {
  // BOM so Excel shows Arabic correctly
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}